import {
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  Heading,
  HStack,
  Icon,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  SimpleGrid,
  Table,
  Tag,
  TagCloseButton,
  TagLabel,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react'
import React, { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { LuChevronDown, LuChevronRight, LuClipboardList, LuExternalLink, LuInfo, LuLock } from 'react-icons/lu'
import BlotRecentCases, {
  parsedBlots,
  type BlotAnalysis,
} from '../BlotAnalysisWorkspace/BlotRecentCases'
import { IMMUNOBLOTS, resolvePathologyLabel } from '../BlotAnalysisWorkspace/ImmunoBlotSelection'
import { getImmunoblotConfigs } from '../../../api/immunoblot-configs'
import { useMyProfileWithModules } from '../../../api/api_hooks/user/myProfile'
import { CHROME_HEADER_GRADIENT } from '../../../theme/chrome'
import { orderedBandNames } from '../BlotAnalysisWorkspace/utils/bandRules'
import {
  bandPairsOf,
  comparisonRows,
  verdictPairsOf,
} from '../BlotAnalysisWorkspace/utils/retrospectivePairs'
import {
  agreementOf,
  kappaLabel,
  positiveNegativeAgreement,
  type Agreement,
  type Proportion,
} from '../BlotAnalysisWorkspace/utils/agreementStats'

/**
 * Le registre d'évaluation : tous les cas lancés, les filtres de la liste
 * récente plus la pathologie, et — l'étage qui justifie la page — les mesures
 * d'accord AGRÉGÉES sur le périmètre filtré.
 *
 * Trois décisions structurent ce fichier, prises avec Aniss le 29/10 :
 *
 * 1. L'UNITÉ D'AGRÉGATION EST LA BANDELETTE, PAS LE CAS. On concatène les
 *    paires (paillasse, IRIS) de toutes les bandelettes du périmètre puis on
 *    calcule l'accord sur ce total — jamais une moyenne de kappas par cas, qui
 *    ferait peser un cas de 3 bandelettes autant qu'un de 43 et trébucherait
 *    sur les cas au kappa indéfini. L'extraction des paires est LE MÊME code
 *    que la fiche d'un cas (utils/retrospectivePairs) : les deux affichages ne
 *    peuvent pas se contredire.
 *
 * 2. SEULS LES CAS RÉTROSPECTIFS VERROUILLÉS ENTRENT DANS L'ACCORD. Un cas
 *    prospectif a montré IRIS avant la lecture humaine : son « accord » est
 *    contaminé par le biais d'automatisation. La liste du bas montre tout ce
 *    que le filtre demande ; l'agrégat, lui, ne compte que les cas où les deux
 *    lectures ont été figées à l'aveugle, et il affiche son dénominateur.
 *
 * 3. LA PATHOLOGIE S'ATTRIBUE PAR BANDELETTE. Une plaque Dynablot porte
 *    plusieurs recherches à la fois : ses bandelettes sont réparties dans le
 *    tableau via leur propre libellé, résolu par le même dictionnaire de
 *    mots-clés que la galerie — pas via la pathologie du cas.
 *
 * Tout se calcule côté client, depuis la page que /recent-cases renvoie déjà
 * (benchReading, irisSnapshot, blots y sont). C'est délibéré : rien de nouveau
 * à déployer, le registre reste local avec le reste du module rétrospectif.
 * La réserve est celle de la liste : l'assiette s'arrête à `limit` cas — le
 * dénominateur affiché dit sur quoi portent les chiffres. Le jour où les cas
 * se comptent en centaines, l'agrégation passera côté serveur et ce fichier
 * ne portera plus que l'affichage.
 */

/** Les mêmes mesures, cas par cas — les sous-lignes du tableau déplié. */
interface CaseAggregate {
  id: string
  label: string
  date: string
  strips: number
  band: Agreement
  verdict: Agreement
  ppa: Proportion | null
  npa: Proportion | null
}

interface PathologyRow {
  key: string
  label: string
  caseCount: number
  strips: number
  band: Agreement
  verdict: Agreement
  ppa: Proportion | null
  npa: Proportion | null
  /** PPA/NPA au niveau des CONCLUSIONS (par bandelette) — le critère
   *  principal d'un dossier ; les colonnes bandes expliquent le pourquoi. */
  verdictPpa: Proportion | null
  verdictNpa: Proportion | null
  /** Déplié au clic : chaque cas de la pathologie, mêmes colonnes, et un
   *  accès direct au cas. */
  cases: CaseAggregate[]
}

// Couleur STABLE par pathologie pour les tags de l'agrégat : attribuée sur
// l'ordre alphabétique des clés (l'entité, jamais le rang du moment — une
// recherche garde sa couleur quand une autre entre ou sort du périmètre).
const CHIP_COLORS = ['teal', 'orange', 'blue', 'pink', 'cyan', 'green', 'red', 'yellow'] as const

// Libellé lisible d'un profil de détection — le proxy CHU des cas.
const chuLabel = (id: string): string => {
  if (id === 'default') return 'Défaut'
  if (id === 'aphp') return 'AP-HP'
  if (id === 'protis') return 'PROTIS'
  return id
    .split('-')
    .map((w) => (w === 'chu' ? 'CHU' : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ')
}

const eligible = (a: BlotAnalysis): boolean =>
  (a.evaluationMode ?? 'prospective') === 'retrospective'
  && !!a.benchReading?.lockedAt
  && !!a.benchReading?.blots
  && !!a.irisSnapshot?.blots

const pct = (v: number) => `${(v * 100).toFixed(0)} %`
const ciText = (ci: [number, number] | null) =>
  ci ? `[${pct(ci[0])} – ${pct(ci[1])}]` : ''

function MetricCard({ label, value, detail, info }: {
  label: string
  value: string
  detail?: string
  info?: string
}) {
  return (
    <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100" p={4}>
      <HStack spacing={1} mb={1}>
        <Text fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase" letterSpacing="wide">
          {label}
        </Text>
        {info && (
          <Tooltip label={info} hasArrow maxW="320px">
            <Box as="span" display="inline-flex"><Icon as={LuInfo} boxSize={3} color="gray.400" /></Box>
          </Tooltip>
        )}
      </HStack>
      <Text fontSize="2xl" fontWeight="800" color="gray.800" lineHeight={1}>{value}</Text>
      {detail && <Text fontSize="xs" color="gray.500" mt={1}>{detail}</Text>}
    </Box>
  )
}

/** En-tête de colonne avec son ⓘ : chaque mesure du registre s'explique
 *  sur place — un tableau d'accord ne doit pas supposer le jargon connu. */
function ThInfo({ label, info, isNumeric }: { label: string; info: string; isNumeric?: boolean }) {
  return (
    <Th isNumeric={isNumeric}>
      <Tooltip label={info} hasArrow maxW="330px" placement="top">
        <Box as="span" display="inline-flex" alignItems="center" gap="4px" cursor="help">
          {label}
          <Icon as={LuInfo} boxSize={3} color="gray.400" />
        </Box>
      </Tooltip>
    </Th>
  )
}

export default function BlotRegistry() {
  const navigate = useNavigate()
  // Le panneau d'accueil (« + N autres cas ») arrive avec une intention :
  // liste dépliée et filtre déjà posé (statut 'todo' = tout sauf validé).
  const location = useLocation()
  const navIntent = (location.state ?? {}) as { openList?: boolean; statusFilter?: string }
  const [pathologyFilter, setPathologyFilter] = useState<string>('all')
  // Sélection multiple pour l'AGRÉGAT : les pathologies cochées au-dessus des
  // cartes. Vide = toutes. L'agrégat porte sur le périmètre CHARGÉ : les
  // filtres de tri de la liste (statut, mode, pathologie) ne le touchent pas —
  // seuls période et scope admin, qui définissent l'assiette, s'appliquent
  // aux deux. Ici on choisit librement « Aspergillus + Toxo » sans toucher à
  // la liste du bas.
  const [selectedPathos, setSelectedPathos] = useState<Set<string>>(new Set())
  const togglePatho = useCallback((key: string) => {
    setSelectedPathos((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])
  // Pareil par CHU (profil de détection des cas) — utile à l'admin seul, dont
  // les cas de test traversent plusieurs CHU ; un pilote n'a que le sien.
  const { data: myProfile } = useMyProfileWithModules()
  const isAdmin = myProfile?.role === 'admin' || myProfile?.role === 'super_admin'
  const [selectedChus, setSelectedChus] = useState<Set<string>>(new Set())
  const toggleChu = useCallback((key: string) => {
    setSelectedChus((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])
  // La ligne du tableau d'accord actuellement dépliée (ses cas, un par un).
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  // Les deux étages lourds de la page se replient indépendamment : le détail
  // par pathologie et la liste des cas. La liste RESTE MONTÉE repliée
  // (Collapse ne démonte pas) — c'est elle qui fournit l'assiette de l'agrégat.
  const [detailOpen, setDetailOpen] = useState(true)
  // Repliée par défaut : le registre s'ouvre sur les chiffres, la liste se
  // déplie à la demande (elle reste montée, voir plus bas).
  const [listOpen, setListOpen] = useState(() => Boolean(navIntent.openList))
  // Plage de dates de l'AGRÉGAT (du / au, bornes incluses) — un affinage du
  // haut, comme les pathologies et le CHU, qui ne touche pas la liste.
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  // `visible` = la liste telle que triée en bas (pour son compteur) ;
  // `loaded` = le périmètre chargé, l'assiette de l'agrégat.
  const [visible, setVisible] = useState<BlotAnalysis[]>([])
  const onVisibleChange = useCallback((list: BlotAnalysis[]) => setVisible(list), [])
  const [loaded, setLoaded] = useState<BlotAnalysis[]>([])
  const onLoadedChange = useCallback((list: BlotAnalysis[]) => setLoaded(list), [])

  // Toutes les notices actives en un appel, indexées par leur `pathology` —
  // qui est, par contrat du catalogue, exactement le `name` des entrées
  // d'IMMUNOBLOTS (« la recherche se fait par égalité stricte côté serveur »).
  const { data: configsData } = useQuery({
    queryKey: ['immunoblotConfigs', 'all-active'],
    queryFn: async () => (await getImmunoblotConfigs({ activeOnly: true })).data.data,
    staleTime: 5 * 60 * 1000,
  })
  const bandNamesByPathology = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const config of configsData ?? []) {
      map.set(config.pathology, orderedBandNames(config as any))
    }
    return map
  }, [configsData])

  const { rows, available, availableChus, blinding, flow, ensemble, lockedCases, eligibleLoaded } = useMemo(() => {
    type CasePairs = {
      id: string
      label: string
      date: string
      strips: number
      bandPairs: Array<[boolean, boolean]>
      verdictPairs: Array<[boolean, boolean]>
    }
    const groups = new Map<string, {
      key: string
      label: string
      cases: Set<string>
      bandPairs: Array<[boolean, boolean]>
      verdictPairs: Array<[boolean, boolean]>
      strips: number
      perCase: Map<string, CasePairs>
    }>()
    // Plage de dates appliquée en premier : les compteurs des sélecteurs
    // (pathologies, CHU) décrivent alors la période choisie, pas tout le chargé.
    const fromTs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null
    const toTs = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null
    const eligibleAll = loaded.filter(eligible)
    const locked = eligibleAll.filter((a) => {
      const ts = new Date(a.createdAt).getTime()
      if (fromTs !== null && ts < fromTs) return false
      if (toTs !== null && ts > toTs) return false
      return true
    })

    // Les CHU réellement présents parmi les cas verrouillés, pour le sélecteur
    // admin — comptés AVANT le filtre CHU, pour pouvoir décocher.
    const chuCounts = new Map<string, number>()
    for (const a of locked) {
      const p = a.detectionProfile || 'default'
      chuCounts.set(p, (chuCounts.get(p) ?? 0) + 1)
    }
    const availableChus = Array.from(chuCounts.entries())
      .map(([key, count]) => ({ key, label: chuLabel(key), count }))
      .sort((x, y) => y.count - x.count)

    const inChuScope = selectedChus.size
      ? locked.filter((a) => selectedChus.has(a.detectionProfile || 'default'))
      : locked

    // Comment l'aveugle a été obtenu, compté sur le périmètre analysé. Un cas
    // sans mention date d'avant que le champ existe : il est compté comme
    // imposé, puisque le voile était alors la seule voie possible.
    const blinding = inChuScope.reduce(
      (acc, a) => {
        if (a.benchReading?.blinding === 'declared') acc.declared += 1
        else acc.enforced += 1
        return acc
      },
      { enforced: 0, declared: 0 },
    )

    // Le flux, côté CHU : une seule grandeur l'intéresse, le DÉNOMINATEUR —
    // combien de bandelettes d'échantillon ces plaques portaient, face à
    // combien sont entrées dans la comparaison. Un taux d'accord sans ce
    // chiffre ne dit pas sur quelle fraction de la série il porte, et une
    // bandelette perdue en route se lit alors comme une bonne performance.
    // Le détail — identifiants corrigés, lignes de contrôle repositionnées —
    // mesure IRIS et non le laboratoire : il reste au panneau admin.
    //
    // Les témoins désignés et les faux positifs masqués sortent du compte :
    // les premiers ont une réponse connue d'avance, les seconds ne sont pas
    // des bandelettes.
    const flow = inChuScope.reduce(
      (acc, a) => {
        const controls = [
          a.selectedPositiveControlId,
          a.selectedPositiveControl2Id,
          a.selectedPositiveControl3Id,
        ].filter((v) => v != null).length
        const detected = a.totalBlots ?? (Array.isArray(a.blots) ? a.blots.length : 0)
        acc.samples += Math.max(0, detected - controls - (a.hiddenBlotIds?.length ?? 0))
        return acc
      },
      { samples: 0 },
    )

    for (const a of inChuScope) {
      const benchBlots = a.benchReading!.blots!
      const irisBlots = a.irisSnapshot!.blots!
      const caseEntry = IMMUNOBLOTS.find((b) => b.id === a.pathologyId) ?? null
      const labelById = new Map<string, string | null>(
        parsedBlots(a).map((b: any) => [String(b?.id), b?.pathology_label ?? null]),
      )

      // Répartir les bandelettes comparables du cas par pathologie propre.
      const byEntry = new Map<string, { ids: string[]; label: string; name: string | null }>()
      for (const id of Object.keys(benchBlots)) {
        if (!irisBlots[id]) continue
        const rawLabel = labelById.get(id)
        // Une bandelette SANS libellé appartient au cas mono-pathologie :
        // c'est le chemin normal. Une bandelette ÉTIQUETÉE qui ne se résout
        // pas (Toxocara LCR/HA…) va dans « Autres » — la rattacher à sa
        // plaque ferait une ligne « Dynablot multitest » dans un tableau de
        // pathologies, et ce n'en est pas une.
        const resolved = rawLabel ? resolvePathologyLabel(rawLabel) : caseEntry
        const key = resolved?.id ?? '__autres__'
        const label = resolved?.displayName ?? resolved?.name ?? 'Autres'
        const bucket = byEntry.get(key) ?? { ids: [], label, name: resolved?.name ?? null }
        bucket.ids.push(id)
        byEntry.set(key, bucket)
      }

      byEntry.forEach((bucket, key) => {
        const pick = (side: Record<string, any>) =>
          Object.fromEntries(bucket.ids.map((id: string) => [id, side[id]]))
        // Sans notice (pathologie non résolue, PROTIS, CIP apparié…), pas de
        // catalogue de bandes : l'accord bande à bande n'est pas calculable,
        // celui des conclusions l'est toujours.
        const bandNames = bucket.name ? bandNamesByPathology.get(bucket.name) ?? [] : []
        const caseRows = comparisonRows(pick(benchBlots), pick(irisBlots), bandNames)
        if (!caseRows.length) return
        const group = groups.get(key) ?? {
          key,
          label: bucket.label,
          cases: new Set<string>(),
          bandPairs: [] as Array<[boolean, boolean]>,
          verdictPairs: [] as Array<[boolean, boolean]>,
          strips: 0,
          perCase: new Map<string, CasePairs>(),
        }
        group.cases.add(a.id)
        group.strips += caseRows.length
        const bandPairs = bandPairsOf(caseRows)
        const verdictPairs = verdictPairsOf(caseRows)
        group.bandPairs.push(...bandPairs)
        group.verdictPairs.push(...verdictPairs)
        // Le même cas peut repasser (plaque multitest figée recherche par
        // recherche) : on accumule, on ne remplace pas.
        const perCase = group.perCase.get(a.id) ?? {
          id: a.id,
          label: a.sampleId || a.filename,
          date: a.createdAt,
          strips: 0,
          bandPairs: [] as Array<[boolean, boolean]>,
          verdictPairs: [] as Array<[boolean, boolean]>,
        }
        perCase.strips += caseRows.length
        perCase.bandPairs.push(...bandPairs)
        perCase.verdictPairs.push(...verdictPairs)
        group.perCase.set(a.id, perCase)
        groups.set(key, group)
      })
    }

    // Les tags listent tout ce que le périmètre CONTIENT ; la sélection ne
    // restreint que ce qui se CALCULE (cartes + tableau). Une sélection vide
    // vaut « toutes », et une clé cochée qui a disparu du périmètre (filtre de
    // liste resserré depuis) est simplement ignorée.
    const allGroups = Array.from(groups.values())
    const chipKeys = allGroups.map((g) => g.key).sort()
    const available = allGroups
      .map((g) => ({
        key: g.key,
        label: g.label,
        strips: g.strips,
        color: CHIP_COLORS[chipKeys.indexOf(g.key) % CHIP_COLORS.length],
      }))
      .sort((a, b) => b.strips - a.strips)
    const activeGroups = selectedPathos.size
      ? allGroups.filter((g) => selectedPathos.has(g.key))
      : allGroups

    const rows: PathologyRow[] = activeGroups
      .map((g) => ({
        key: g.key,
        label: g.label,
        caseCount: g.cases.size,
        strips: g.strips,
        band: agreementOf(g.bandPairs),
        verdict: agreementOf(g.verdictPairs),
        ...(() => {
          const pna = positiveNegativeAgreement(g.bandPairs)
          // Et au niveau RÉSULTAT — le critère principal d'un dossier : parmi
          // vos bandelettes positives, celles qu'IRIS conclut positives, et
          // le miroir pour les négatives.
          const pnaVerdict = positiveNegativeAgreement(g.verdictPairs)
          return { ppa: pna.positive, npa: pna.negative, verdictPpa: pnaVerdict.positive, verdictNpa: pnaVerdict.negative }
        })(),
        cases: Array.from(g.perCase.values())
          .map((c) => ({
            id: c.id,
            label: c.label,
            date: c.date,
            strips: c.strips,
            band: agreementOf(c.bandPairs),
            verdict: agreementOf(c.verdictPairs),
            ...(() => {
              const pna = positiveNegativeAgreement(c.bandPairs)
              return { ppa: pna.positive, npa: pna.negative }
            })(),
          }))
          .sort((x, y) => new Date(y.date).getTime() - new Date(x.date).getTime()),
      }))
      .sort((a, b) => b.strips - a.strips)

    const allBandPairs = activeGroups.flatMap((g) => g.bandPairs)
    const allVerdictPairs = activeGroups.flatMap((g) => g.verdictPairs)
    const caseIds = new Set(activeGroups.flatMap((g) => Array.from(g.cases)))
    const pna = positiveNegativeAgreement(allBandPairs)
    return {
      rows,
      available,
      availableChus,
      blinding,
      flow,
      ensemble: {
        caseCount: caseIds.size,
        strips: rows.reduce((s, r) => s + r.strips, 0),
        band: agreementOf(allBandPairs),
        verdict: agreementOf(allVerdictPairs),
        ppa: pna.positive,
        npa: pna.negative,
      },
      lockedCases: locked.length,
      // Pour afficher la rangée de sélecteurs même quand la plage de dates a
      // tout vidé — sinon « Effacer » disparaîtrait avec elle.
      eligibleLoaded: eligibleAll.length,
    }
  }, [loaded, bandNamesByPathology, selectedPathos, selectedChus, dateFrom, dateTo])

  const kappaText = (a: Agreement) =>
    a.kappa === null ? '—' : a.kappa.toFixed(2)

  return (
    <Box h="100vh" overflow="hidden" bg="gray.50" p={6}>
      <VStack spacing={5} align="stretch" h="100%" overflowY="auto">
        {/* Bandeau : le même métal sombre que l'accueil et le Lab Studio
            (CHROME_HEADER_GRADIENT) — le registre est une page transversale,
            il porte la livrée de la maison, pas celle d'un module. */}
        <Box
          bgGradient={CHROME_HEADER_GRADIENT}
          borderRadius="2xl"
          px={6}
          py={5}
          boxShadow="2xl"
          flexShrink={0}
        >
          <HStack spacing={4}>
            <Box p={3} bg="whiteAlpha.300" backdropFilter="blur(10px)" borderRadius="xl" border="1px solid" borderColor="whiteAlpha.400">
              <Icon as={LuClipboardList} boxSize={7} color="white" />
            </Box>
            <VStack spacing={0.5} align="start">
              <Heading size="md" color="white" fontWeight="bold" letterSpacing="tight">
                Registre d'évaluation
              </Heading>
              <Text fontSize="sm" color="whiteAlpha.800">
                Tous les cas lancés, et l'accord paillasse / IRIS agrégé sur le périmètre chargé
              </Text>
            </VStack>
          </HStack>
        </Box>

        {/* L'étage des chiffres */}
        <Box bg="purple.50" borderRadius="2xl" border="1px solid" borderColor="purple.100" p={5}>
          <HStack justify="space-between" flexWrap="wrap" mb={4} gap={2}>
            <HStack spacing={2}>
              <Icon as={LuLock} color="purple.600" boxSize={4} />
              <Text fontSize="sm" fontWeight="700" color="purple.800">
                Accord sur les lectures figées à l'aveugle
              </Text>
              <Badge colorScheme="purple" variant="subtle" borderRadius="md">
                {lockedCases} cas verrouillé{lockedCases > 1 ? 's' : ''} sur {loaded.length} chargé{loaded.length > 1 ? 's' : ''}
              </Badge>
              {/* Comment l'aveugle a été obtenu, sur l'ensemble du périmètre.
                  Un voile imposé par le logiciel et un aveugle simplement
                  déclaré ne pèsent pas le même poids dans un dossier : la
                  vue d'ensemble doit permettre de séparer les deux
                  populations, et d'abord de savoir qu'elles existent.
                  Affiché même quand tout est imposé : « aucun aveugle
                  déclaré » n'est pas du bruit, c'est l'argument. */}
              {lockedCases > 0 && (
                <Tooltip
                  label={
                    blinding.declared > 0
                      ? `${blinding.enforced} cas lus sous voile imposé par le logiciel, ${blinding.declared} en aveugle déclaré par l'opérateur. À séparer à l'analyse : seul le voile imposé garantit l'ordre des deux lectures.`
                      : `Les ${blinding.enforced} cas ont tous été lus sous voile imposé : la saisie de paillasse a été verrouillée avant que la lecture d'IRIS ne devienne visible. C'est la forme d'aveugle la plus solide dans un dossier, et elle se mentionne telle quelle.`
                  }
                  hasArrow
                  maxW="340px"
                >
                  <Badge
                    colorScheme={blinding.declared > 0 ? 'orange' : 'teal'}
                    variant="subtle"
                    borderRadius="md"
                    cursor="help"
                  >
                    {blinding.declared > 0
                      ? `${blinding.enforced} imposé${blinding.enforced > 1 ? 's' : ''} · ${blinding.declared} déclaré${blinding.declared > 1 ? 's' : ''}`
                      : 'tous en aveugle imposé'}
                  </Badge>
                </Tooltip>
              )}
            </HStack>
            <Text fontSize="xs" color="purple.700" maxW="520px">
              Seuls comptent les cas rétrospectifs dont la saisie de paillasse a été
              verrouillée puis IRIS figé : un cas prospectif a montré IRIS avant la
              lecture humaine, son accord ne mesure pas l'outil. Les tris de la
              liste en dessous ne changent pas ces chiffres — la période, si.
            </Text>
            {/* Le dénominateur du périmètre. Masqué dès qu'un filtre de
                pathologie est actif : les bandelettes comparées seraient alors
                restreintes à la recherche choisie alors que le dénominateur
                porte sur toute la plaque, et le rapport serait faux. */}
            {selectedPathos.size === 0 && flow.samples > 0 && (
              <Text fontSize="xs" color="purple.700" maxW="520px">
                Ces plaques portaient <b>{flow.samples} bandelettes d'échantillon</b> ;{' '}
                <b>{ensemble.strips}</b> sont entrées dans la comparaison
                {flow.samples > 0 ? ` (${Math.round((ensemble.strips / flow.samples) * 100)} %)` : ''}.
                {ensemble.strips < flow.samples && " Les autres n'ont pas de lecture de paillasse appariée : elles comptent dans la série, pas dans l'accord."}
              </Text>
            )}
          </HStack>

          {/* Choix des pathologies de l'AGRÉGAT : un sélecteur compact plutôt
              qu'un mur de tags — le périmètre peut porter beaucoup de
              recherches. Seules les pathologies COCHÉES s'affichent en tags
              (bornés par le choix de l'utilisateur), retirables d'une croix.
              Aucune cochée = toutes comptent. Indépendant du sélecteur de la
              liste en dessous. */}
          {eligibleLoaded > 0 && (
            <HStack mb={4} spacing={2} flexWrap="wrap" align="center">
              {/* Admin : restreindre l'accord à un ou plusieurs CHU (profil de
                  détection des cas) — les cas de test d'un admin traversent
                  plusieurs pilotes, ceux d'un pilote n'en ont qu'un. */}
              {isAdmin && availableChus.length > 1 && (
                <Popover placement="bottom-start">
                  <PopoverTrigger>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="purple"
                      bg="white"
                      rightIcon={<Icon as={LuChevronDown} boxSize={3.5} />}
                    >
                      {selectedChus.size === 0
                        ? `CHU : tous (${availableChus.length})`
                        : `CHU : ${selectedChus.size}/${availableChus.length}`}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent w="280px">
                    <PopoverArrow />
                    <PopoverBody maxH="320px" overflowY="auto" py={3}>
                      <VStack align="stretch" spacing={2}>
                        <HStack justify="space-between">
                          <Text fontSize="xs" color="gray.500">
                            Aucun coché, tous comptent.
                          </Text>
                          {selectedChus.size > 0 && (
                            <Button
                              size="xs"
                              variant="link"
                              colorScheme="purple"
                              flexShrink={0}
                              onClick={() => setSelectedChus(new Set())}
                            >
                              Tout décocher
                            </Button>
                          )}
                        </HStack>
                        {availableChus.map((c) => (
                          <Checkbox
                            key={c.key}
                            size="sm"
                            colorScheme="purple"
                            isChecked={selectedChus.has(c.key)}
                            onChange={() => toggleChu(c.key)}
                          >
                            <HStack spacing={2}>
                              <Text fontSize="sm" noOfLines={1}>{c.label}</Text>
                              <Text fontSize="xs" color="gray.500" flexShrink={0}>
                                · {c.count} cas
                              </Text>
                            </HStack>
                          </Checkbox>
                        ))}
                      </VStack>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              )}
              {isAdmin &&
                availableChus
                  .filter((c) => selectedChus.has(c.key))
                  .map((c) => (
                    <Tag key={c.key} size="sm" colorScheme="purple" borderRadius="full" variant="subtle">
                      <TagLabel>{c.label}</TagLabel>
                      <TagCloseButton onClick={() => toggleChu(c.key)} />
                    </Tag>
                  ))}
              {available.length > 1 && (
              <Popover placement="bottom-start">
                <PopoverTrigger>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="purple"
                    bg="white"
                    rightIcon={<Icon as={LuChevronDown} boxSize={3.5} />}
                  >
                    {selectedPathos.size === 0
                      ? `Pathologies : toutes (${available.length})`
                      : `Pathologies : ${selectedPathos.size}/${available.length}`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent w="330px">
                  <PopoverArrow />
                  <PopoverBody maxH="320px" overflowY="auto" py={3}>
                    <VStack align="stretch" spacing={2}>
                      <HStack justify="space-between">
                        <Text fontSize="xs" color="gray.500">
                          Cochez les recherches à compter dans l'accord — aucune
                          cochée, toutes comptent.
                        </Text>
                        {selectedPathos.size > 0 && (
                          <Button
                            size="xs"
                            variant="link"
                            colorScheme="purple"
                            flexShrink={0}
                            onClick={() => setSelectedPathos(new Set())}
                          >
                            Tout décocher
                          </Button>
                        )}
                      </HStack>
                      {available.map((p) => (
                        <Checkbox
                          key={p.key}
                          size="sm"
                          colorScheme="purple"
                          isChecked={selectedPathos.has(p.key)}
                          onChange={() => togglePatho(p.key)}
                        >
                          <HStack spacing={2}>
                            <Box w="8px" h="8px" borderRadius="full" bg={`${p.color}.400`} flexShrink={0} />
                            <Text fontSize="sm" noOfLines={1}>{p.label}</Text>
                            <Text fontSize="xs" color="gray.500" flexShrink={0}>· {p.strips}</Text>
                          </HStack>
                        </Checkbox>
                      ))}
                    </VStack>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
              )}
              {available
                .filter((p) => selectedPathos.has(p.key))
                .map((p) => (
                  <Tag key={p.key} size="sm" colorScheme={p.color} borderRadius="full" variant="subtle">
                    <TagLabel>{p.label}</TagLabel>
                    <TagCloseButton onClick={() => togglePatho(p.key)} />
                  </Tag>
                ))}
              {/* Plage de dates de l'agrégat — bornes incluses, l'une ou
                  l'autre peut rester vide. */}
              <HStack spacing={1.5} flexShrink={0}>
                <Text fontSize="xs" color="purple.700">Du</Text>
                <Input
                  type="date"
                  size="sm"
                  bg="white"
                  w="150px"
                  borderRadius="md"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
                <Text fontSize="xs" color="purple.700">au</Text>
                <Input
                  type="date"
                  size="sm"
                  bg="white"
                  w="150px"
                  borderRadius="md"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                />
                {(dateFrom || dateTo) && (
                  <Button
                    size="xs"
                    variant="link"
                    colorScheme="purple"
                    onClick={() => { setDateFrom(''); setDateTo('') }}
                  >
                    Effacer
                  </Button>
                )}
              </HStack>
            </HStack>
          )}

          {ensemble.strips === 0 ? (
            <Text fontSize="sm" color="purple.700" py={4} textAlign="center">
              Aucune lecture comparable dans ce périmètre — élargissez la période
              ou les pathologies / CHU cochés, ou verrouillez des cas
              rétrospectifs.
            </Text>
          ) : (
            <>
              <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} mb={4}>
                <MetricCard
                  label="Bandelettes"
                  value={String(ensemble.strips)}
                  detail={`${ensemble.caseCount} cas · ${ensemble.band.n} paires bande`}
                />
                <MetricCard
                  label="Accord bandes"
                  value={pct(ensemble.band.observed)}
                  detail={ciText(ensemble.band.observedCi)}
                  info="Bande à bande, sur le catalogue de la notice : vue/non-vue des deux côtés. IC 95 % (Wilson)."
                />
                <MetricCard
                  label="Kappa"
                  value={kappaText(ensemble.band)}
                  detail={ensemble.band.kappa !== null
                    ? `${kappaLabel(ensemble.band.kappa)}${ensemble.band.ci ? ` · ${ciText(ensemble.band.ci)}` : ''}`
                    : ensemble.band.undefinedReason ?? undefined}
                  info="Kappa de Cohen sur les paires bande : l'accord corrigé du hasard."
                />
                <MetricCard
                  label="IRIS retrouve"
                  value={ensemble.ppa ? pct(ensemble.ppa.value) : '—'}
                  detail={ensemble.ppa ? `des ${ensemble.ppa.n} bandes vues à la paillasse ${ciText(ensemble.ppa.ci)}` : 'aucune bande vue à la paillasse'}
                  info="Accord positif (PPA) : parmi les bandes que le biologiste a vues, celles qu'IRIS retrouve."
                />
                <MetricCard
                  label="Accord conclusions"
                  value={pct(ensemble.verdict.observed)}
                  detail={`${ensemble.verdict.n} bandelette${ensemble.verdict.n > 1 ? 's' : ''} conclue${ensemble.verdict.n > 1 ? 's' : ''} ${ciText(ensemble.verdict.observedCi)}`}
                  info="Positif/négatif par bandelette, les deux conclusions venant des mêmes règles de notice."
                />
              </SimpleGrid>

              {/* Par pathologie — repliable et plafonné en hauteur (en-tête
                  collant) : avec beaucoup de recherches, ce tableau ne doit
                  pas pousser la liste des cas hors de l'écran. */}
              <Button
                size="xs"
                variant="ghost"
                colorScheme="purple"
                mb={2}
                leftIcon={
                  <Icon
                    as={LuChevronRight}
                    boxSize={3.5}
                    transform={detailOpen ? 'rotate(90deg)' : undefined}
                    transition="transform 0.15s"
                  />
                }
                onClick={() => setDetailOpen((v) => !v)}
              >
                Détail par pathologie ({rows.length})
              </Button>
              <Collapse in={detailOpen} animateOpacity>
              <Box
                bg="white"
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.100"
                overflowX="auto"
                maxH="380px"
                overflowY="auto"
              >
                <Table size="sm" variant="simple">
                  <Thead position="sticky" top={0} bg="white" zIndex={1} boxShadow="0 1px 0 var(--chakra-colors-gray-100)">
                    <Tr>
                      <ThInfo
                        label="Pathologie"
                        info="La recherche, attribuée bandelette par bandelette : une plaque multitest compte dans chacune des recherches qu'elle porte. Cliquez une ligne pour la déplier en ses cas."
                      />
                      <ThInfo
                        isNumeric
                        label="Cas / date"
                        info="Le nombre de cas verrouillés qui nourrissent la ligne ; une fois dépliée, la date de chaque cas."
                      />
                      <ThInfo
                        isNumeric
                        label="Bandelettes"
                        info="Les bandelettes comparées : votre lecture de paillasse verrouillée face à celle d'IRIS figée au dévoilement."
                      />
                      <ThInfo
                        isNumeric
                        label="Accord bandes"
                        info="Bande à bande, sur le catalogue de la notice : vue ou non vue, des deux côtés. Entre crochets, l'intervalle de confiance à 95 % (méthode de Wilson)."
                      />
                      <ThInfo
                        isNumeric
                        label="κ"
                        info="Kappa de Cohen : l'accord corrigé de ce que le hasard produirait seul. Repères (Landis & Koch) : moins de 0,40 faible · 0,41–0,60 modéré · 0,61–0,80 substantiel · plus de 0,80 presque parfait."
                      />
                      <ThInfo
                        isNumeric
                        label="IRIS retrouve"
                        info="Accord positif (PPA) : parmi les bandes que vous avez vues à la paillasse, la part qu'IRIS retrouve. Répond à « IRIS rate-t-il des bandes ? »"
                      />
                      <ThInfo
                        isNumeric
                        label="Absences confirmées"
                        info="Accord négatif (NPA) : parmi les bandes que vous n'avez PAS vues, la part qu'IRIS déclare aussi absentes. Répond à « IRIS invente-t-il des bandes ? »"
                      />
                      <ThInfo
                        isNumeric
                        label="Accord conclusions"
                        info="Positif ou négatif, par bandelette — les deux conclusions déduites des mêmes règles de notice. En dessous : PPA = parmi vos bandelettes POSITIVES, celles qu'IRIS conclut positives ; NPA = le miroir pour les négatives. C'est le critère principal d'un dossier ; les colonnes bandes expliquent le pourquoi."
                      />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((r) => {
                      const chipColor = available.find((p) => p.key === r.key)?.color ?? 'gray'
                      return (
                      <React.Fragment key={r.key}>
                        {/* La ligne se déplie au clic : les mêmes mesures,
                            cas par cas. Le filtre, lui, reste au sélecteur
                            de la liste en dessous. */}
                        <Tr
                          cursor="pointer"
                          bg={expandedKey === r.key ? 'purple.50' : undefined}
                          _hover={{ bg: expandedKey === r.key ? 'purple.50' : 'gray.50' }}
                          onClick={() => setExpandedKey(expandedKey === r.key ? null : r.key)}
                        >
                          <Td fontWeight="600">
                            <HStack spacing={1.5}>
                              <Icon
                                as={LuChevronRight}
                                boxSize={3.5}
                                color="gray.400"
                                transform={expandedKey === r.key ? 'rotate(90deg)' : undefined}
                                transition="transform 0.15s"
                              />
                              {/* La pastille reprend la couleur du tag de la
                                  pathologie au-dessus : même entité, même couleur. */}
                              <Box w="8px" h="8px" borderRadius="full" bg={`${chipColor}.400`} flexShrink={0} />
                              <Text as="span">{r.label}</Text>
                            </HStack>
                          </Td>
                          <Td isNumeric>{r.caseCount}</Td>
                          <Td isNumeric>{r.strips}</Td>
                          <Td isNumeric>
                            {r.band.n ? `${pct(r.band.observed)} ${ciText(r.band.observedCi)}` : '—'}
                          </Td>
                          <Td isNumeric>
                            <Tooltip label={r.band.kappa !== null ? kappaLabel(r.band.kappa) : r.band.undefinedReason ?? ''} hasArrow>
                              <Text as="span">{kappaText(r.band)}</Text>
                            </Tooltip>
                          </Td>
                          <Td isNumeric>{r.ppa ? `${pct(r.ppa.value)} (n=${r.ppa.n})` : '—'}</Td>
                          <Td isNumeric>{r.npa ? pct(r.npa.value) : '—'}</Td>
                          <Td isNumeric>
                            {r.verdict.n ? (
                              <Box>
                                <Text as="span">{`${pct(r.verdict.observed)} (${r.verdict.n})`}</Text>
                                {(r.verdictPpa || r.verdictNpa) && (
                                  <Text fontSize="2xs" color="gray.500">
                                    {r.verdictPpa ? `PPA ${pct(r.verdictPpa.value)} (n=${r.verdictPpa.n})` : ''}
                                    {r.verdictPpa && r.verdictNpa ? ' · ' : ''}
                                    {r.verdictNpa ? `NPA ${pct(r.verdictNpa.value)} (n=${r.verdictNpa.n})` : ''}
                                  </Text>
                                )}
                              </Box>
                            ) : '—'}
                          </Td>
                        </Tr>
                        {expandedKey === r.key && r.cases.map((c) => (
                          <Tr
                            key={c.id}
                            bg="gray.50"
                            cursor="pointer"
                            _hover={{ bg: 'purple.50' }}
                            onClick={() => navigate(`/lab/blot-analysis/${c.id}`, { state: { from: 'registry' } })}
                            title="Ouvrir le cas"
                          >
                            <Td pl={9} py={1.5}>
                              <HStack spacing={1.5} maxW="340px">
                                <Icon as={LuExternalLink} boxSize={3} color="purple.400" flexShrink={0} />
                                <Text as="span" fontSize="xs" color="gray.700" noOfLines={1}>
                                  {c.label}
                                </Text>
                              </HStack>
                            </Td>
                            <Td isNumeric py={1.5} fontSize="xs" color="gray.500">
                              {new Date(c.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                            </Td>
                            <Td isNumeric py={1.5} fontSize="xs">{c.strips}</Td>
                            <Td isNumeric py={1.5} fontSize="xs">
                              {c.band.n ? `${pct(c.band.observed)} ${ciText(c.band.observedCi)}` : '—'}
                            </Td>
                            <Td isNumeric py={1.5} fontSize="xs">
                              <Tooltip label={c.band.kappa !== null ? kappaLabel(c.band.kappa) : c.band.undefinedReason ?? ''} hasArrow>
                                <Text as="span">{kappaText(c.band)}</Text>
                              </Tooltip>
                            </Td>
                            <Td isNumeric py={1.5} fontSize="xs">{c.ppa ? `${pct(c.ppa.value)} (n=${c.ppa.n})` : '—'}</Td>
                            <Td isNumeric py={1.5} fontSize="xs">{c.npa ? pct(c.npa.value) : '—'}</Td>
                            <Td isNumeric py={1.5} fontSize="xs">
                              {c.verdict.n ? `${pct(c.verdict.observed)} (${c.verdict.n})` : '—'}
                            </Td>
                          </Tr>
                        ))}
                      </React.Fragment>
                      )
                    })}
                  </Tbody>
                </Table>
              </Box>
              </Collapse>
            </>
          )}
        </Box>

        {/* La liste — le même composant que partout, filtres compris.
            Repliable mais jamais démontée : c'est elle qui fournit `visible`,
            l'assiette de tout l'étage d'accord au-dessus. */}
        <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.100" p={5} pb={listOpen ? 8 : 5}>
          <HStack
            spacing={1.5}
            mb={listOpen ? 3 : 0}
            cursor="pointer"
            onClick={() => setListOpen((v) => !v)}
            role="button"
            aria-expanded={listOpen}
          >
            <Icon
              as={LuChevronRight}
              boxSize={4}
              color="gray.400"
              transform={listOpen ? 'rotate(90deg)' : undefined}
              transition="transform 0.15s"
            />
            <Text fontWeight="700" fontSize="sm" color="gray.700">
              Tous les cas
            </Text>
            <Badge colorScheme="gray" variant="subtle" borderRadius="md">
              {visible.length}
            </Badge>
          </HStack>
          <Collapse in={listOpen} animateOpacity>
          <BlotRecentCases
            limit={100}
            light
            showFilters
            module="iris"
            caseLinkState={{ from: 'registry' }}
            pathologyFilter={pathologyFilter}
            onPathologyFilterChange={setPathologyFilter}
            onVisibleChange={onVisibleChange}
            onLoadedChange={onLoadedChange}
            initialStatusFilter={navIntent.statusFilter as any}
            onCaseSelect={(a) => navigate(`/lab/blot-analysis/${a.id}`, { state: { from: 'registry' } })}
          />
          </Collapse>
        </Box>
      </VStack>
    </Box>
  )
}
